<script setup>
import { useSlots } from 'vue';
import CustomBrandPolicyWrapper from 'dashboard/components/CustomBrandPolicyWrapper.vue';
import { getHelpUrlForFeature } from '../../../../helper/featureHelper';
import BackButton from '../../../../components/widgets/BackButton.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  linkText: {
    type: String,
    default: '',
  },
  featureName: {
    type: String,
    default: '',
  },
  backButtonLabel: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
});

const slots = useSlots();

const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const helpURL = getHelpUrlForFeature(props.featureName);
</script>

<template>
  <div
    class="flex w-full flex-col gap-4 rounded-[24px] border border-lais-border bg-white p-5 shadow-lais-card sm:p-6"
  >
    <BackButton
      v-if="backButtonLabel"
      compact
      :button-label="backButtonLabel"
      class="mb-1"
    />
    <div
      v-if="title"
      class="flex w-full flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
    >
      <div class="min-w-0 flex-1">
        <h1
          class="font-redHatDisplay text-[24px] font-semibold text-lais-text sm:text-[28px]"
        >
          {{ title }}
        </h1>
        <div
          v-if="description || $slots.description || linkText || helpURL"
          class="mt-2 flex flex-col gap-2 text-lais-text-body"
        >
          <p
            v-if="description || $slots.description"
            class="mb-0 max-w-3xl text-[14px] leading-6"
          >
            <slot name="description">{{ description }}</slot>
          </p>
          <CustomBrandPolicyWrapper :show-on-custom-branded-instance="false">
            <a
              v-if="helpURL && linkText"
              :href="helpURL"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex w-fit items-center gap-1 text-[13px] font-semibold text-lais-primary hover:underline"
            >
              {{ linkText }}
              <Icon
                icon="i-lucide-chevron-right"
                class="size-4 flex-shrink-0 text-lais-primary"
              />
            </a>
          </CustomBrandPolicyWrapper>
        </div>
      </div>
      <div
        v-if="slots.count || slots.actions"
        class="flex w-full flex-wrap items-center gap-3 lg:w-auto lg:justify-end"
      >
        <slot name="count" />
        <slot name="actions" />
      </div>
    </div>
    <div
      v-if="searchPlaceholder || slots.tabs"
      class="flex flex-col gap-3 border-t border-lais-border-soft pt-4 md:flex-row md:items-center md:justify-between"
    >
      <div
        class="flex min-w-0 flex-1 flex-col gap-3 md:flex-row md:items-center"
      >
        <slot name="tabs" />
        <Input
          v-if="searchPlaceholder"
          v-model="searchQuery"
          :placeholder="searchPlaceholder"
          class="group w-full md:max-w-[320px] [&>input]:!h-11 [&>input]:!rounded-lais [&>input]:!border-lais-border [&>input]:!bg-white [&>input]:!text-lais-text [&>input]:ltr:!pl-10 [&>input]:rtl:!pr-10 [&>input]:focus:!border-lais-primary"
          size="sm"
          type="search"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute top-1/2 size-4 -translate-y-1/2 text-lais-text-muted ltr:left-3 rtl:right-3"
            />
          </template>
        </Input>
      </div>
    </div>
  </div>
</template>
